import { cn, getBackgroundClass } from '@workspace/lib';
import { ResolvedLink } from '@workspace/ui';
import { Icon, Picture, Typography } from '@workspace/ui/common';
import { featuredCardsVariants } from '@workspace/blocks/FeaturedCards/featuredCards.variants';

import type { CardSizes, FeaturedCardsProps } from './featured-cards.types';

const cardSizes: CardSizes = {
  2: ['large', 'large'],
  3: ['large', 'medium', 'medium'],
  4: ['large', 'medium', 'small', 'small'],
  5: ['large', 'small', 'small', 'small', 'small'],
};

const imageDimensions = {
  small: { desktopWidth: 288, desktopHeight: 384 },
  medium: { desktopWidth: 592, desktopHeight: 370 },
  large: { desktopWidth: 592, desktopHeight: 888 },
} as const;

export function FeaturedCards({ block }: FeaturedCardsProps) {
  if (!block) return null;

  const { cards, backgroundColor, heading, subheading } = block;
  if (!cards) return null;

  const sizes = cardSizes[cards.length];
  if (!sizes) return null;

  const layout = cards.length === 2 ? 'pair' : 'mosaic';
  const {
    container,
    header,
    heading: headingSlot,
    subheading: subheadingSlot,
    list,
  } = featuredCardsVariants({
    layout,
  });

  return (
    <section className={cn('overflow-hidden w-full', getBackgroundClass(backgroundColor ?? 'bg-white'))}>
      <div className={cn('porta-container flex flex-col w-full', container())}>
        <header className={cn('flex flex-col', header())}>
          <Typography variant="h2" className={headingSlot()}>
            {heading}
          </Typography>
          {subheading?.trim() && (
            <Typography variant="bodyLg" className={subheadingSlot()}>
              {subheading}
            </Typography>
          )}
        </header>

        <div
          className={cn(
            'grid auto-cols-[18rem] grid-flow-col md:auto-cols-auto md:grid-cols-4 md:grid-flow-row',
            'overflow-x-auto overflow-y-hidden md:overflow-visible',
            'scrollbar-hide scroll-smooth snap-x snap-mandatory',
            list(),
          )}
        >
          {cards.map((card, index) => {
            const size = sizes[index];
            const dimensions = imageDimensions[layout === 'pair' ? 'large' : size];
            const hasLink = Boolean(card.url);
            const {
              card: cardSlot,
              imageWrapper,
              image: imageSlot,
              content,
              text,
              link,
              arrow,
            } = featuredCardsVariants({ layout, size, hasLink });

            return (
              <article
                key={card._key ?? index}
                className={cn(
                  'group relative',
                  'md:row-span-2 md:grid md:grid-rows-subgrid',
                  'min-w-0',
                  'snap-start md:snap-none',
                  cardSlot(),
                )}
              >
                <div className={cn('relative overflow-hidden w-full', imageWrapper())}>
                  <Picture
                    image={card.image ?? undefined}
                    {...dimensions}
                    mobileWidth={288}
                    mobileHeight={432}
                    fill
                    imgClassName={cn(
                      'object-cover',
                      'size-full',
                      'transition-transform duration-300 ease-in-out',
                      'motion-reduce:transform-none',
                      imageSlot(),
                    )}
                  />
                </div>
                <div className={cn('flex items-center self-start min-w-0', content())}>
                  <Typography variant={size === 'small' ? 'h4' : 'h3'} className={text()}>
                    <ResolvedLink
                      link={card}
                      className={cn(
                        'after:absolute after:inset-0',
                        'focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2',
                        link(),
                      )}
                    >
                      {card.text}
                    </ResolvedLink>
                  </Typography>
                  {hasLink && (
                    <Icon
                      name="arrowRightIcon"
                      size={size === 'small' ? 32 : 40}
                      className={cn(
                        'shrink-0',
                        'transition-transform duration-300 ease-in-out',
                        'group-hover:translate-x-0.5 motion-reduce:transform-none',
                        arrow(),
                      )}
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
